import { t } from 'i18n';
import React from 'react';
import { useState } from 'react';
import { Loading } from 'components/Loading';
import {
  ChangeAutogenProductPropModal,
  ChangeDynamicProductPropModal,
} from './ChangeProductPropModal';
import { ComboItem } from 'types';
import {
  BenefitItems,
  BenefitMoney,
  BenefitVoucher,
  ComboDetailPayload,
} from '@teko/discovery';
import { useDetailCombo } from 'hooks';
import { ProductPrice, BlockHeader } from '@teko/teko-ecommerce';
import {
  CashColored,
  GiftColored,
  VoucherColored,
  DownOutlined,
} from '@teko/ds-icon';
import { useScreen } from 'contexts';
import { trackManually, getTrackingData } from '@teko/tracking';
import {
  Button,
  SubTitle,
  Currency,
  Modal,
  LazyImage,
  styled,
  Body,
  Caption,
  Card,
} from '@teko/ds-desktop';
import { Quantity } from 'components/Quantity';
interface DetailComboModalProps extends ComboDetailPayload {
  visible: boolean;
  onCloseModal: () => void | Promise<void>;
  onAddToCart: (
    type: ComboItem['type'],
    payload: ComboDetailPayload,
  ) => void | Promise<void>;
  type: ComboItem['type'];
  loading?: boolean;
}
export const DetailComboModal: React.FC<DetailComboModalProps> = ({
  visible,
  onCloseModal,
  onAddToCart,
  type,
  loading = false,
  ...rest
}: DetailComboModalProps) => {
  const [visibleUpdateProduct, setVisibleUpdateProduct] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState<number>();
  const {
    products,
    supplierRetailPrice,
    latestPrice,
    onChangeQuantity,
    comboPromotion,
    onFinish,
    loadingCombo,
    onChangeAutogenProduct,
    onChangeDynamicProduct,
    payloadCombo,
  } = useDetailCombo(type, rest);
  const { trackingScreenName } = useScreen();
  const renderProductItemDiscount = (discount: BenefitMoney, size = 'md') => {
    if (!discount) return null;
    let text;
    if (discount.percent) {
      text = (
        <Body level={1}>
          {t('Giảm')}
          {discount.percent}%{' '}
          {discount.maxAmount ? (
            <>
              {t('tối đa')}
              <Currency marginRight="s4" value={discount.maxAmount} />
            </>
          ) : (
            ''
          )}
          {t(' khi mua combo')}
        </Body>
      );
    } else {
      text = (
        <Body level={1} className="att-detail-combo-promotion-discount-flag">
          {t('Giảm trực tiếp')} <Currency value={discount.flat} />
          {t(' khi mua combo')}
        </Body>
      );
    }
    return (
      <StyledPromotion
        style={{
          fontSize: size === 'md' ? 13 : 12,
          lineHeight: size === 'md' ? '20px' : '16px',
        }}
      >
        <WrapperIcon size={size === 'md' ? 40 : 32} bgColor="#E7FBED">
          <CashColored size={24} />
        </WrapperIcon>
        {text}
      </StyledPromotion>
    );
  };
  const renderProductItemGifts = (
    gifts: BenefitItems[],
    size = 'md',
    quantity = 1,
  ) => {
    return gifts.map((gift) => (
      <StyledPromotion
        key={gift.sku}
        style={{
          fontSize: size === 'md' ? 13 : 12,
          lineHeight: size === 'md' ? '20px' : '16px',
        }}
      >
        <WrapperIcon size={size === 'md' ? 40 : 32}>
          <LazyImage
            src={gift.imageUrl}
            width={size === 'md' ? 40 : 32}
            imageWidth={100}
            height={size === 'md' ? 40 : 32}
            borderRadius={4}
            contain
          />
        </WrapperIcon>
        <Body level={1}>
          {gift.quantity * quantity} x {gift.name}
        </Body>
      </StyledPromotion>
    ));
  };
  const renderProductItemVouchers = (
    vouchers: BenefitVoucher[],
    size = 'md',
    quantity = 1,
  ) => {
    return vouchers.map((voucher) => (
      <StyledPromotion
        key={voucher.name}
        style={{
          fontSize: size === 'md' ? 13 : 12,
          lineHeight: size === 'md' ? '20px' : '16px',
        }}
      >
        <WrapperIcon size={size === 'md' ? 40 : 32} bgColor="#FFF2F0">
          <VoucherColored size={24} />
        </WrapperIcon>
        <Body level={1}>
          {voucher.quantity * quantity} x {voucher.name}
        </Body>
      </StyledPromotion>
    ));
  };
  const handleAddToCart = async () => {
    await onFinish(onAddToCart);
  };
  return (
    <Modal show={visible} onHide={onCloseModal}>
      <Card>
        <BlockHeader title={t('Chi tiết combo')} />
        <Loading isLoading={loadingCombo || loading}>
          <WrapperBodyModal>
            <div style={{ position: 'relative' }}>
              {products.map((product, index, list) => {
                const {
                  selectedVariant,
                  allowSelectPromotion,
                  latestPrice: latestPriceProduct,
                  supplierRetailPrice: supplierRetailPriceProduct,
                  gifts,
                  vouchers,
                  image,
                  sku,
                  quantity,
                  totalAvailable,
                  name,
                } = product;
                const textVariant = selectedVariant.join(' | ');
                const showTextSelectPromotion =
                  !textVariant && allowSelectPromotion;
                const hasPromotions = !!gifts.length || !!vouchers.length;
                return (
                  <StyledProduct
                    key={index}
                    className={`${
                      index === list.length - 1 ? 'last-item' : ''
                    } ${index === 0 ? 'first-item' : ''}`}
                  >
                    <StyledProductInfo>
                      <StyledProductInfoLeft>
                        <StyledProductImage
                          src={image}
                          width={80}
                          imageWidth={150}
                          height={80}
                          borderRadius={4}
                          contain
                        />
                        <div style={{ marginLeft: 12 }}>
                          <Body
                            level={1}
                            line={2}
                            className={`att-detail-combo-product-name-${index}`}
                          >
                            {name}
                          </Body>
                          {!!textVariant || showTextSelectPromotion ? (
                            <VariantWrapper
                              onClick={() => {
                                trackManually(
                                  'updateProduct',
                                  'productComboDetail',
                                  trackingScreenName || 'ProductDetail',
                                  JSON.stringify({
                                    sku,
                                    screenName: trackingScreenName,
                                    index,
                                    skus: products.map((el) => el.sku),
                                    promotioinId: comboPromotion?.id,
                                    type,
                                  }),
                                );
                                setSelectedIdx(index);
                                setVisibleUpdateProduct(true);
                              }}
                            >
                              {!!textVariant && (
                                <Caption marginRight="s4" color="#82869E">
                                  {textVariant}
                                </Caption>
                              )}
                              {showTextSelectPromotion && (
                                <Caption marginRight="s4" color="#82869E">
                                  {t('Chọn khuyến mại')}
                                </Caption>
                              )}
                              <DownOutlined size={18} />
                            </VariantWrapper>
                          ) : null}
                        </div>
                      </StyledProductInfoLeft>
                      <StyledProductInfoRight>
                        <Quantity
                          disabled={type === 'dynamic'}
                          setQuantity={(val) => onChangeQuantity(sku, val)}
                          quantity={quantity}
                          min={1}
                          max={totalAvailable || undefined}
                        />
                        <WrapperPrices>
                          <ProductPrice
                            latestPrice={latestPriceProduct}
                            supplierRetailPrice={supplierRetailPriceProduct}
                            textColor="#434657"
                            className={`att-detail-combo-product-price-${index}`}
                          />
                        </WrapperPrices>
                      </StyledProductInfoRight>
                    </StyledProductInfo>
                    {hasPromotions && (
                      <WrapperPromotions>
                        {renderProductItemGifts(gifts, 'sm', quantity)}
                        {renderProductItemVouchers(vouchers, 'sm', quantity)}
                      </WrapperPromotions>
                    )}
                  </StyledProduct>
                );
              })}
              {comboPromotion ? (
                <StyledComboPromotion>
                  <StyledComboPromotionTitle>
                    <GiftColored size={24} />
                    <Body level={2} weight="medium" marginLeft="s4">
                      {t('Quà tặng kèm')}
                    </Body>
                  </StyledComboPromotionTitle>
                  <WrapperPromotions
                    style={{ marginTop: 8, padding: '16px 16px 0' }}
                  >
                    {renderProductItemDiscount(comboPromotion.benefit.discount)}
                    {renderProductItemGifts(comboPromotion.benefit.gifts || [])}
                    {renderProductItemVouchers(
                      comboPromotion.benefit?.voucher?.name
                        ? [comboPromotion.benefit.voucher]
                        : [],
                    )}
                  </WrapperPromotions>
                </StyledComboPromotion>
              ) : null}
              {(!selectedIdx || !!products[selectedIdx]) && type === 'autogen'
                ? selectedIdx !== undefined && (
                    <ChangeAutogenProductPropModal
                      index={selectedIdx}
                      product={products[selectedIdx]}
                      visible={visibleUpdateProduct && type === 'autogen'}
                      onChangeAutogenProduct={onChangeAutogenProduct}
                      onCloseModal={() => {
                        setVisibleUpdateProduct(false);
                        setSelectedIdx(undefined);
                      }}
                    />
                  )
                : null}
              {selectedIdx !== undefined &&
              !!products[selectedIdx] &&
              type === 'dynamic' ? (
                <ChangeDynamicProductPropModal
                  selectedProductIdx={selectedIdx}
                  visible={visibleUpdateProduct && type === 'dynamic'}
                  onChangeDynamicProduct={onChangeDynamicProduct}
                  payload={payloadCombo}
                  onCloseModal={() => {
                    setVisibleUpdateProduct(false);
                    setSelectedIdx(undefined);
                  }}
                />
              ) : null}
            </div>
          </WrapperBodyModal>
          <WrapperButtonGroup>
            <ProductPrice
              size="l"
              supplierRetailPrice={supplierRetailPrice}
              latestPrice={latestPrice || ''}
              className="att-detail-combo-grandtotal"
            />
            <div style={{ display: 'flex' }}>
              <Button
                onClick={onCloseModal}
                style={{ paddingLeft: '3rem', paddingRight: '3rem' }}
              >
                <SubTitle level={2} weight="medium">
                  {t('Bỏ qua')}
                </SubTitle>
              </Button>
              <AddComboButtonWrapper
                {...getTrackingData({
                  isTrackContent: true,
                  regionName: 'productComboDetail',
                  contentName: 'addToCart',
                  target: trackingScreenName,
                  payload: JSON.stringify({
                    skus: products.map((el) => el.sellerSku),
                    promotionId: rest.promotionId,
                    screenName: trackingScreenName,
                    type,
                  }),
                })}
              >
                <Button
                  onClick={() => handleAddToCart()}
                  marginLeft="s8"
                  variant="primary"
                  paddingLeft="s32"
                  paddingRight="s32"
                >
                  <SubTitle level={2} weight="medium">
                    {t('Thêm vào giỏ')}
                  </SubTitle>
                </Button>
              </AddComboButtonWrapper>
            </div>
          </WrapperButtonGroup>
        </Loading>
      </Card>
    </Modal>
  );
};
const WrapperBodyModal = styled.div`
  position: relative;
  padding: 1.5rem;
  max-height: 35rem;
  overflow: auto;
  border-bottom: 1px dashed rgb(221, 221, 221);
  border-top: 1px dashed rgb(221, 221, 221);
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: #f5f5f5;
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #f5f5f5;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #999;
  }
`;
const WrapperButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
`;
const StyledProduct = styled.div`
  padding: 16px 0;
  border-bottom: 1px dashed #e4e5f0;
  &.last-item {
    border-bottom: none;
  }
  &.first-item {
    padding-top: 0;
  }
`;
const StyledProductInfo = styled.div`
  display: flex;
`;
const StyledProductInfoLeft = styled.div`
  display: flex;
  flex: 6;
`;
const StyledProductImage = styled(LazyImage)`
  max-width: 80px;
  margin: unset;
`;
const StyledProductInfoRight = styled.div`
  display: flex;
  flex: 4;
  justify-content: space-between;
  align-items: flex-start;
  padding-left: 24px;
`;
const WrapperPrices = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
const VariantWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;
const WrapperPromotions = styled.div`
  background-color: #f8f8fc;
  padding: 12px 12px 0;
  margin-top: 16px;
  border-radius: 8px;
`;
const StyledPromotion = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 12px;
`;
interface WrapperIconProps {
  bgColor?: string;
  size: number;
}
const WrapperIcon = styled.div<WrapperIconProps>(
  ({ bgColor, size }: WrapperIconProps) => `
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${bgColor || 'unset'};
  width: ${size}px;
  height: ${size}px;
  margin-right: 8px;
  border-radius: 4px;
`,
);
const StyledComboPromotion = styled.div`
  padding-top: 16px;
  border-top: 1px solid #e4e5f0;
`;
const StyledComboPromotionTitle = styled.div`
  display: flex;
  align-items: center;
`;

const AddComboButtonWrapper = styled.div``;
